import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../services/api';
import { validateUsername } from '../utils/validation';
import Spinner from './Spinner';
import TermsAndPrivacy from './TermsAndPrivacy';

const SignupWithInvite: React.FC = () => {
    const [email, setEmail] = useState('');
    const [userName, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [usernameValidation, setUsernameValidation] = useState<{
        isValid: boolean;
        error?: string;
    }>({ isValid: true });
    const navigate = useNavigate();
    const location = useLocation();

    // Extract invite code from URL query string and check if user is logged in
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const codeFromUrl =
            urlParams.get('invite') || urlParams.get('code') || urlParams.get('inviteCode');
        if (codeFromUrl) {
            setInviteCode(codeFromUrl);
        }

        // Check if user is already logged in
        const checkAuthAndAcceptInvite = async () => {
            try {
                const accountResponse = await authApi.getAccountInfo();
                if (accountResponse.status === 'success' && codeFromUrl) {
                    // User is logged in and has an invite code, accept the invite and redirect
                    const acceptResponse = await authApi.acceptInvite({ inviteCode: codeFromUrl });
                    if (acceptResponse.status === 'success') {
                        // Redirect to game page with gameId
                        const gameUrl = new URL(import.meta.env.VITE_REACT_APP_GAME_URL);
                        gameUrl.searchParams.set('t', new Date().getTime().toString());
                        if (acceptResponse.gameId) {
                            gameUrl.searchParams.set('gameId', acceptResponse.gameId);
                        }
                        window.location.href = gameUrl.toString();
                        return;
                    } else {
                        setError(acceptResponse.message || 'Failed to accept invite');
                    }
                }
            } catch (err) {
                // User is not logged in, continue with normal signup flow
                console.log('User not authenticated, proceeding with signup');
            } finally {
                setCheckingAuth(false);
            }
        };

        checkAuthAndAcceptInvite();
    }, [location.search]);

    // Real-time username validation
    useEffect(() => {
        if (userName.trim()) {
            const validation = validateUsername(userName);
            setUsernameValidation(validation);
        } else {
            setUsernameValidation({ isValid: true });
        }
    }, [userName]);

    const validateForm = (): string | null => {
        if (!email.trim()) {
            return 'Email is required';
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return 'Please enter a valid email address';
        }

        // Validate username using common validation function
        const usernameValidation = validateUsername(userName);
        if (!usernameValidation.isValid) {
            return usernameValidation.error!;
        }

        if (!password) {
            return 'Password is required';
        }

        if (password.length < 8) {
            return 'Password must be at least 8 characters long';
        }

        if (!confirmPassword) {
            return 'Password confirmation is required';
        }

        if (password !== confirmPassword) {
            return 'Passwords do not match';
        }

        if (!inviteCode.trim()) {
            return 'Invite code is required';
        }

        if (inviteCode.length !== 16) {
            return 'Invite code must be exactly 6 characters';
        }

        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);

        try {
            const response = await authApi.signup({
                email: email.trim(),
                userName: userName.trim(),
                password,
                confirmPassword,
                gameInviteId: inviteCode.trim(),
            });

            if (response.status === 'success') {
                setSuccess('Account created successfully! You can now log in.');
                // Clear form
                setEmail('');
                setUserName('');
                setPassword('');
                setConfirmPassword('');
                setInviteCode('');

                // Redirect to login after a short delay
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } else {
                setError(response.message || 'Signup failed');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleInviteCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.slice(0, 16); // Restrict to 6 characters
        setInviteCode(value);
    };

    // Show loading spinner while checking authentication
    if (checkingAuth) {
        return (
            <div
                className='min-h-screen flex items-center justify-center'
                style={{ backgroundColor: '#1D1A31' }}
            >
                <div className='flex flex-col items-center'>
                    <Spinner size='lg' color='blue' />
                    <p className='mt-4 text-gray-600'>Checking authentication...</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className='min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8'
            style={{ backgroundColor: '#1D1A31' }}
        >
            <div className='max-w-md w-full space-y-8'>
                {/* Header */}
                <div className='text-center'>
                    <h1 className='text-4xl font-bold text-orange-500 mb-2'>Join Puzzle Planet</h1>
                    <p className='text-lg text-white'>Create your account with an invite code</p>
                </div>

                {/* Signup Form */}
                <div className='bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100'>
                    <form onSubmit={handleSubmit} className='space-y-6'>
                        {/* Email Field */}
                        <div>
                            <label
                                htmlFor='email'
                                className='block text-sm font-medium text-gray-700 mb-2'
                            >
                                Email Address
                            </label>
                            <input
                                type='email'
                                id='email'
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                disabled={loading}
                                placeholder='Enter your email'
                                autoComplete='off'
                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                            />
                        </div>

                        {/* Username Field */}
                        <div>
                            <label
                                htmlFor='userName'
                                className='block text-sm font-medium text-gray-700 mb-2'
                            >
                                Username
                            </label>
                            <input
                                type='text'
                                id='userName'
                                value={userName}
                                onChange={e => setUserName(e.target.value)}
                                required
                                disabled={loading}
                                placeholder='Choose a username (4-20 chars, letters & numbers only)'
                                autoComplete='off'
                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-offset-0 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed ${
                                    userName.trim() && !usernameValidation.isValid
                                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                        : userName.trim() && usernameValidation.isValid
                                        ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                }`}
                            />
                            {/* Username validation message */}
                            {userName.trim() && !usernameValidation.isValid && (
                                <p className='mt-1 text-sm text-red-600'>
                                    {usernameValidation.error}
                                </p>
                            )}
                        </div>

                        {/* Password Field */}
                        <div>
                            <label
                                htmlFor='password'
                                className='block text-sm font-medium text-gray-700 mb-2'
                            >
                                Password
                            </label>
                            <input
                                type='password'
                                id='password'
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                                disabled={loading}
                                placeholder='Enter a password (min 8 characters)'
                                minLength={8}
                                autoComplete='new-password'
                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                            />
                        </div>

                        {/* Confirm Password Field */}
                        <div>
                            <label
                                htmlFor='confirmPassword'
                                className='block text-sm font-medium text-gray-700 mb-2'
                            >
                                Confirm Password
                            </label>
                            <input
                                type='password'
                                id='confirmPassword'
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                required
                                disabled={loading}
                                placeholder='Confirm your password'
                                autoComplete='new-password'
                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                            />
                        </div>

                        {/* Invite Code Field */}
                        <div>
                            <label
                                htmlFor='inviteCode'
                                className='block text-sm font-medium text-gray-700 mb-2'
                            >
                                Invite Code
                            </label>
                            <input
                                type='text'
                                id='inviteCode'
                                value={inviteCode}
                                onChange={handleInviteCodeChange}
                                required
                                disabled={loading}
                                placeholder='Enter 16-character invite code'
                                maxLength={16}
                                style={{ textTransform: 'uppercase' }}
                                autoComplete='off'
                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed font-mono tracking-wider'
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'>
                                {error}
                            </div>
                        )}

                        {/* Success Message */}
                        {success && (
                            <div className='bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm'>
                                {success}
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type='submit'
                            disabled={loading}
                            className='w-full bg-gradient-to-r from-yellow-400 to-orange-500 !text-black font-semibold py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
                        >
                            {loading ? (
                                <div className='flex items-center justify-center'>
                                    <Spinner size='md' color='black' className='-ml-1 mr-3' />
                                    Creating Account...
                                </div>
                            ) : (
                                'Create Account'
                            )}
                        </button>
                        <TermsAndPrivacy />
                    </form>

                    {/* Sign In Link */}
                    <div className='mt-6 text-center'>
                        <p className='text-sm text-gray-600'>
                            Already have an account?{' '}
                            <button
                                type='button'
                                onClick={() => {
                                    const loginUrl = inviteCode
                                        ? `/login?inviteCode=${inviteCode}`
                                        : '/login';
                                    navigate(loginUrl);
                                }}
                                disabled={loading}
                                className='text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 disabled:opacity-50'
                            >
                                Sign in here
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignupWithInvite;
